import { effectiveUserRoles, type UserRole } from "@the-drop/types";

/**
 * **Platform admin** may pick any transporter via session scope only when they are **not** also
 * **`transporter_dispatcher`** — staff stay scoped to their license/org.
 */
export function shouldShowAdminTransporterScopePicker(user: {
  role: UserRole;
  roles?: UserRole[];
}): boolean {
  const rs = effectiveUserRoles(user);
  return rs.includes("platform_admin") && !rs.includes("transporter_dispatcher");
}

/** Who may open the transporter ops workspace (§15.3). Drivers use **`driver-mobile`**, not this app. */
export function isTransporterOpsRole(role: UserRole): boolean {
  return role === "platform_admin" || role === "transporter_dispatcher";
}

/** Multi-role: access if **any** effective role may use the transporter ops shell. */
export function isTransporterOpsUser(user: { role: UserRole; roles?: UserRole[] }): boolean {
  return effectiveUserRoles(user).some((r) => isTransporterOpsRole(r));
}

export function resolveTransporterIdForWorkspace(
  role: UserRole,
  userTransporterId: string | undefined,
  adminScopeTransporterId: string | null,
): string | null {
  if (role === "transporter_dispatcher") {
    return userTransporterId?.trim() || null;
  }
  if (role === "platform_admin") {
    const s = adminScopeTransporterId?.trim();
    return s && s.length > 0 ? s : null;
  }
  return null;
}

/**
 * **`transporter_dispatcher`** always uses **`user.transporterId`** (including when **`platform_admin`**
 * is also present). Pure **`platform_admin`** uses session **`adminScopeTransporterId`**.
 */
export function resolveTransporterIdForWorkspaceUser(
  user: { role: UserRole; roles?: UserRole[]; transporterId?: string },
  adminScopeTransporterId: string | null,
): string | null {
  const rs = effectiveUserRoles(user);
  if (rs.includes("transporter_dispatcher")) {
    return user.transporterId?.trim() || null;
  }
  if (rs.includes("platform_admin")) {
    const s = adminScopeTransporterId?.trim();
    return s && s.length > 0 ? s : null;
  }
  return null;
}
